import React, { useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchUnreadCount } from '../store/slices/messagesSlice';
import logo from '../assets/logo.png';
import './Layout.css';

const countryCodeToFlag = (code?: string): string => {
  if (!code || code.length !== 2) return '🏳️';
  return code.toUpperCase().split('').map(c =>
    String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)
  ).join('');
};

const getScopeIcon = (scope: string, countryCode?: string) => {
  switch (scope) {
    case 'city': return '🏙️';
    case 'state': return '🗺️';
    case 'country': return countryCodeToFlag(countryCode);
    default: return '🌍';
  }
};

const Layout: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { unreadCount } = useAppSelector((state) => state.messages);
  const { scope } = useAppSelector((state) => state.posts);
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin/');

  useEffect(() => {
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  return (
    <div className={`layout${isAdminPage ? ' layout-wide' : ''}`}>
      <nav className="sidebar">
        <div className="logo">
          <Link to="/">
            <img src={logo} alt="Meerkapp" className="logo-img" />
            <h1>Meerkapp</h1>
          </Link>
        </div>
        
        <ul className="nav-links">
          <li>
            <Link to="/" className="nav-link">
              <span className="icon">{getScopeIcon(scope, user?.country_code)}</span>
              <span>Home</span>
            </Link>
          </li>
          <li>
            <Link to="/messages" className="nav-link">
              <span className="icon">
                💬
                {unreadCount > 0 && <span className="nav-badge">{unreadCount}</span>}
              </span>
              <span>Messages</span>
            </Link>
          </li>
          <li>
            <Link to={`/profile/${user?.id}`} className="nav-link">
              <span className="icon">👤</span>
              <span>Profile</span>
            </Link>
          </li>
          {user?.is_admin && (
            <>
              <li>
                <Link to="/admin/feeds" className="nav-link">
                  <span className="icon">📡</span>
                  <span>Feeds</span>
                </Link>
              </li>
              <li>
                <Link to="/admin/analytics" className="nav-link">
                  <span className="icon">📊</span>
                  <span>Analytics</span>
                </Link>
              </li>
              <li>
                <Link to="/admin/online" className="nav-link">
                  <span className="icon">👥</span>
                  <span>Online</span>
                </Link>
              </li>
            </>
          )}
        </ul>

      </nav>

      <main className="main-content">
        <Outlet />
      </main>

      {!isAdminPage && (
        <aside className="right-sidebar">
          <div className="card">
            <h3>Trust Rating</h3>
            <p className="trust-info">
              Your trust rating: <strong className="trust-high">{user?.trust_rating || 5}/10</strong>
            </p>
            <small>Higher ratings = more visibility</small>
          </div>
        </aside>
      )}
    </div>
  );
};

export default Layout;
